import { RUNNING } from 'constants/campaignStates';
import styled from 'styled-components';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { LinkButton } from 'ui/linkButton';
import { CircleSpinner } from 'ui/spinner';
import { colors, spacing } from 'ui/theme';
import { BottomTooltip, LeftTooltip, TopTooltip } from 'ui/tooltip';

import { useCampaignQueryContext } from '../../context/CampaignQueryContext';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { displayContent } from '../content';
import { MessagingPermissionOptions } from '../types';
import { useGetMessagingPermission } from '../utils';

const TooltipContent = styled.div`
  > * + * {
    margin-top: ${spacing.half};
  }
`;

const StyledCircleSpinner = styled(CircleSpinner)`
  margin-right: ${spacing.threeQuarter};
`;

export const ContinueToReviewButton = () => {
  const { isDirty, hasContent } = useEditableFieldsContext();
  const {
    isBlastMissingAudience,
    isFinishedValidating,
    isSendable,
    localeContentErrors,
    localeContentWarnings,
    hasValidationError,
  } = useErrorValidationContext();
  const { campaign } = useCampaignQueryContext();
  const { id, campaignState } = campaign;
  const messagingPermission = useGetMessagingPermission();
  const isContinueToReviewButtonDisabled = messagingPermission !== MessagingPermissionOptions.ALL;
  const rbacTooltipContent =
    messagingPermission === MessagingPermissionOptions.VIEW_ONLY
      ? displayContent.tooltipViewOnlyPermission
      : displayContent.tooltipViewAndDraftPermission;

  const content = (
    <TooltipContent>
      {!isSendable && <div>{displayContent.errReviewDisabled}</div>}
      {isBlastMissingAudience && <div>{displayContent.errBlastCampaignMissingAudience}</div>}
    </TooltipContent>
  );

  const ChildComponent = (
    <LinkButton
      data-test={'continue-to-review-button'}
      isAngular
      isDisabled={
        isDirty ||
        !hasContent ||
        isContinueToReviewButtonDisabled ||
        !isSendable ||
        isBlastMissingAudience ||
        !!localeContentErrors?.length ||
        !!localeContentWarnings?.length
      }
      size={BUTTON_SIZES.LARGE}
      to={
        campaignState === RUNNING
          ? `/analytics/campaignPerformance?campaignId=${id}`
          : `/campaigns/review?campaignId=${id}`
      }
      variant={BUTTON_VARIANTS.PRIMARY}
    >
      {!isFinishedValidating && <StyledCircleSpinner color={colors.grey50} size={16} />}
      {displayContent.btnContinueToReview}
    </LinkButton>
  );

  /**
   * the tooltips have an order of importance; only one should ever be visible,
   * even if the stars align in such a way that multiple tooltips could show.
   * see https://iterable.atlassian.net/browse/FE-579 for more information, and
   * for a helpful image of which tooltip is the most important.
   */
  if (isContinueToReviewButtonDisabled) {
    return <BottomTooltip tooltipContent={rbacTooltipContent}>{ChildComponent}</BottomTooltip>;
  }

  if (!isFinishedValidating || hasValidationError) {
    return (
      <LeftTooltip
        tooltipContent={
          !isFinishedValidating
            ? displayContent.tooltipValidatingContent
            : displayContent.validationError
        }
      >
        {ChildComponent}
      </LeftTooltip>
    );
  }

  return (
    <TopTooltip isEnabled={!isSendable || isBlastMissingAudience} tooltipContent={content}>
      {ChildComponent}
    </TopTooltip>
  );
};
